/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import * as R from 'ramda';
import * as React from 'react';
import { connect } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import flame from '../../assets/icons/flame.svg';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { getLoading } from '../../core/redux/api/module';
import { IAppState } from '../../core/redux/IAppState';
import { MODAL_SIZE } from '../../layout/modal/modalSize';
import ModalTitleHeader from '../../layout/modal/ModalTitleHeader';
import ModalView, { ModalContent } from '../../layout/modal/ModalView';
import { getGlobalAndLocalResortList } from '../../resortDetails/module';
import { EntitlementHotlist } from '../entitlements/models';
import { GuestDetails } from '../guestDetails';
import { fullName } from '../module';
import HotlistModal, { State as HotlistModalState } from './HotlistModal';
import HotlistTable from './HotlistTable';
import { filterHotlistsByResortList, getFutureActiveHotlistsForGuest, validateHotlistRemove } from './module';
import { removeFromEntitlementHotlistAction } from './removeFromEntitlementHotlistAction';
import ImmutableMap from '../../language/ImmutableMap';
import { EntitlementType } from '../../entitlementTypes/entitlementType';
import { getResource } from '../../language/resources/getResource';


type StateProps = {
  guest: GuestDetails;
  isLoading: boolean;
  entitlementHotlists: EntitlementHotlist[];
  shouldAllowRemoveFromGlobalHotlist: boolean;
  shouldAllowRemoveFromResortHotlist: boolean;
  resources: Record<string, any>;
  dateFormat: string;
  alterraResorts: string[];
};

type DispatchProps = {
  onRemoveFromEntitlementHotlist: (
    entitlementId: string,
    resortAbbreviation: string,
    comments: string,
    entitlementHotlistId: number,
  ) => void;
};

type Props = {
  guestId: string;
  allEntitlementTypes: ImmutableMap<EntitlementType>;

  validateHotlistSubmission?: (givenState: HotlistModalState) => string | undefined;
  onSubmitHandler?: (givenState: HotlistModalState) => void;
  onDismiss: () => void;

  rootCss?: SerializedStyles;
};

type State = {
  hotlistName?: string;
  hotlistId?: number | null;
  hotlistResort?: string;
  hotlistedEntitlementId?: number;
};

export default connect<StateProps, DispatchProps, Props, IAppState>(
  (state: IAppState, props): StateProps => {
    const resorts             = getGlobalAndLocalResortList(state);
    const resortHotlists      = filterHotlistsByResortList(state.entitlementHotlists, resorts);
    const entitlementHotlists = getFutureActiveHotlistsForGuest(state.guests.get(props.guestId), resortHotlists);

    return {
      guest:                              state.guests.get(props.guestId),
      isLoading:                          !!getLoading(state, props.guestId, ACTION_TYPE.IS_ON_HOTLIST_REQUEST),
      entitlementHotlists:                entitlementHotlists,
      shouldAllowRemoveFromGlobalHotlist: state.resortToolsConfig.enableGlobalHotlist,
      shouldAllowRemoveFromResortHotlist: state.resortToolsConfig.enableResortSpecificHotlist,
      resources:                          state.locale,
      dateFormat:                         state.defaultDateFormat,
      alterraResorts: ["AB", "BB", "BM", "CORP", "CR", "DV", "JU", "MM", "SQ", "SZ", "SS", "SVMR", "SN", "SO", "SB", "ST", "SG", "TR", "WP"],
    };
  },
  (dispatch, props): DispatchProps => ({
    onRemoveFromEntitlementHotlist: (
                                      entitlementId: string,
                                      resort: string,
                                      comments: string,
                                      entitlementHotlistId: number,
                                    ) =>
                                      dispatch(removeFromEntitlementHotlistAction(
                                        entitlementId,
                                        props.guestId,
                                        resort,
                                        comments,
                                        entitlementHotlistId,
                                      )),
  }),
)
(class RemoveFromHotlistModal extends React.PureComponent<StateProps & DispatchProps & Props, State> {
  public state: State = {
    hotlistName:            '',
    hotlistId:              null,
    hotlistResort:          '',
    hotlistedEntitlementId: undefined,
  };


  private validateHotlistSubmission = (givenState: HotlistModalState) => validateHotlistRemove(this.props.resources, givenState.comments);


  private onSubmitHandler = (givenState: HotlistModalState) => this.props.onRemoveFromEntitlementHotlist(
    `${this.state.hotlistedEntitlementId!}`,
    this.state.hotlistResort!,
    givenState.comments,
    this.state.hotlistId!,
  );

  private shouldAllowRemove = (hotlistEntry: EntitlementHotlist) => hotlistEntry.isHotlistGlobal
    ? this.props.shouldAllowRemoveFromGlobalHotlist : this.props.shouldAllowRemoveFromResortHotlist;


  private onRemoveClick = (hotlist: EntitlementHotlist) =>
    this.setState({
      hotlistId:              hotlist.entitlementHotlistId,
      hotlistName:            hotlist.title,
      hotlistResort:          hotlist.resortAbbreviation,
      hotlistedEntitlementId: hotlist.entitlementId,
    });

  private clearActiveHotlist = () => this.setState({ hotlistId: null });

  private onSubmitSuccess = this.clearActiveHotlist;

  private onCancelClick = this.clearActiveHotlist;

  private nodeRef = React.createRef<HTMLDivElement>();

  public render = () => {
    const hasHotlistToRemove = !R.isNil(this.state.hotlistId);

    // @ts-ignore
    // @ts-ignore
    return (
      <ModalView
        onDismiss={this.props.onDismiss}
        rootCss={this.props.rootCss}
        size={MODAL_SIZE.LARGE}
        contentCss={css`min-height: 500px;position: relative;`}
      >
        <ModalTitleHeader
          resources={this.props.resources}
          heading={getResource(this.props.resources, 'HOTLIST_REMOVE_MODAL_HEADING')}
          icon={flame}
          onDismiss={this.props.onDismiss}
        />
        <ModalContent css={css`min-height: 500px;position: relative;`}>
          {/* @ts-ignore*/}
          <TransitionGroup component={null} nodeRef={this.nodeRef}>{/* @ts-ignore*/}
            {!hasHotlistToRemove && <CSSTransition
                key='hotlistTable'
                classNames='slide-in-left'
                appear={true}
                timeout={500}
                nodeRef={this.nodeRef}
              >
                <HotlistTable
                  resources={this.props.resources}
                  guestName={fullName(this.props.guest)}
                  isLoading={this.props.isLoading!}
                  data={this.props.entitlementHotlists || []}
                  onRemoveClick={this.onRemoveClick}
                  shouldAllowRemove={this.shouldAllowRemove}
                  rootCss={css`max-width: 944px;`}
                  dateFormat={this.props.dateFormat}
                />
              </CSSTransition>
            }
            {/* @ts-ignore*/}
            {hasHotlistToRemove && <CSSTransition
                key='hotlistModal'
                classNames='slide-in-right'
                unmountOnExit={true}
                mountOnEnter={true}
                appear={true}
                timeout={500}
                nodeRef={this.nodeRef}
              >
                <HotlistModal
                  resources={this.props.resources}
                  guestId={this.props.guestId}
                  allEntitlementTypes={this.props.allEntitlementTypes || ImmutableMap.empty()}
                  alterraResorts={this.props.alterraResorts}
                  validateHotlistSubmission={this.validateHotlistSubmission}
                  onSubmitHandler={this.onSubmitHandler}
                  onSubmitSuccess={this.onSubmitSuccess}
                  onDismiss={this.props.onDismiss}
                  onCancel={this.onCancelClick}
                  hotlistName={this.state.hotlistName}
                  hotlistId={this.state.hotlistId!}
                  submitButtonTitle={getResource(this.props.resources, 'HOTLIST_REMOVE_MODAL_SUBMIT_TITLE', fullName(this.props.guest))}
                  submitButtonText={getResource(this.props.resources, 'HOTLIST_REMOVE_MODAL_HEADING')}
                  cancelButtonTitle={getResource(this.props.resources, 'HOTLIST_REMOVE_MODAL_CANCEL_BUTTON')}
                  rootCss={css`width: 944px;`}
                />
              </CSSTransition>
            }
          </TransitionGroup>
        </ModalContent>
      </ModalView>
    );
  };

});
